// Profile summary/experience formatting: a small Markdown subset.
//
//   # Heading            (whole line)
//   - Bullet item        (flat lists only)
//   **bold**  *italic*  ***both***
//
// A blank line separates blocks; a single newline inside a paragraph is a line break.
// Backslash escapes (\* \# \- \\) keep characters literal. Plain text is valid as-is.
// app/services/profile_markup.py in hoggedin-fastapi mirrors these rules; keep the
// shared fixture (src/utils/__fixtures__/profileMarkupCases.json) identical in both repos.

const HEADING_PATTERN = /^# (.*)$/;
const BULLET_PATTERN = /^- (.*)$/;
const ESCAPABLE = new Set(['\\', '*', '#', '-']);
const isSpace = (char) => char === undefined || /\s/.test(char);

// ---------------------------------------------------------------------------
// Inline parsing: text -> runs of { text, bold, italic }
// ---------------------------------------------------------------------------

const tokenizeInline = (text) => {
  const tokens = [];
  let buffer = '';
  const flush = () => {
    if (buffer) {
      tokens.push({ type: 'text', text: buffer });
      buffer = '';
    }
  };
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '\\' && ESCAPABLE.has(text[index + 1])) {
      buffer += text[index + 1];
      index += 1;
    } else if (char === '*') {
      let end = index;
      while (text[end] === '*') {
        end += 1;
      }
      flush();
      tokens.push({ type: 'stars', count: end - index, prev: text[index - 1], next: text[end] });
      index = end - 1;
    } else {
      buffer += char;
    }
  }
  flush();
  return tokens;
};

const canOpen = (token) => !isSpace(token.next);
const canClose = (token) => !isSpace(token.prev);

// One pass over the tokens. `noOpen` holds indexes of star tokens that may close a mark
// but not open one. Returns the runs, or the index of an opener that was never closed
// (the caller retries with that token added to `noOpen`).
const resolveInline = (tokens, noOpen) => {
  const runs = [];
  let bold = false;
  let italic = false;
  let boldOpener = -1;
  let italicOpener = -1;

  const push = (text) => {
    if (!text) {
      return;
    }
    const last = runs[runs.length - 1];
    if (last && last.bold === bold && last.italic === italic) {
      last.text += text;
    } else {
      runs.push({ text, bold, italic });
    }
  };
  const hasCloserAhead = (from, wantsBold, wantsItalic) =>
    tokens.slice(from + 1).some((token) => {
      if (token.type !== 'stars' || token.count > 3 || !canClose(token)) {
        return false;
      }
      return (wantsBold && token.count >= 2 && token.count <= 3) || (wantsItalic && (token.count === 1 || token.count === 3));
    });
  const tryOpen = (index, token, stars) => {
    if (noOpen.has(index) || !canOpen(token)) {
      return stars;
    }
    if (stars === 3 && !bold && !italic && hasCloserAhead(index, true, true)) {
      bold = true;
      italic = true;
      boldOpener = index;
      italicOpener = index;
      return 0;
    }
    if (stars >= 2 && !bold && hasCloserAhead(index, true, false)) {
      bold = true;
      boldOpener = index;
      stars -= 2;
    }
    if (stars >= 1 && !italic && hasCloserAhead(index, false, true)) {
      italic = true;
      italicOpener = index;
      stars -= 1;
    }
    return stars;
  };

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token.type === 'text') {
      push(token.text);
      continue;
    }
    if (token.count > 3) {
      push('*'.repeat(token.count));
      continue;
    }
    let stars = token.count;
    if (canClose(token)) {
      if (stars === 3 && bold && italic) {
        bold = false;
        italic = false;
        stars = 0;
      } else if (stars >= 2 && bold) {
        bold = false;
        stars -= 2;
      } else if (stars !== 2 && italic) {
        italic = false;
        stars -= 1;
      }
    }
    stars = tryOpen(index, token, stars);
    push('*'.repeat(stars));
  }

  if (bold) {
    return { unclosed: boldOpener };
  }
  if (italic) {
    return { unclosed: italicOpener };
  }
  return { runs };
};

export const parseInline = (text) => {
  const tokens = tokenizeInline(text);
  const noOpen = new Set();
  for (;;) {
    const result = resolveInline(tokens, noOpen);
    if (result.runs) {
      return result.runs;
    }
    noOpen.add(result.unclosed);
  }
};

// ---------------------------------------------------------------------------
// Block parsing: text -> blocks
//   { type: 'paragraph', lines: [runs, ...] }
//   { type: 'heading', runs }
//   { type: 'bulletList', items: [runs, ...] }
// ---------------------------------------------------------------------------

export const parseProfileMarkup = (text) => {
  if (typeof text !== 'string' || !text.trim()) {
    return [];
  }
  const blocks = [];
  let current = null;
  text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((line) => {
      if (!line.trim()) {
        current = null;
        return;
      }
      const heading = line.match(HEADING_PATTERN);
      if (heading) {
        if (heading[1].trim()) {
          blocks.push({ type: 'heading', runs: parseInline(heading[1].trim()) });
        }
        current = null;
        return;
      }
      const bullet = line.match(BULLET_PATTERN);
      if (bullet) {
        if (!bullet[1].trim()) {
          return;
        }
        if (current?.type !== 'bulletList') {
          current = { type: 'bulletList', items: [] };
          blocks.push(current);
        }
        current.items.push(parseInline(bullet[1].trim()));
        return;
      }
      if (current?.type !== 'paragraph') {
        current = { type: 'paragraph', lines: [] };
        blocks.push(current);
      }
      current.lines.push(parseInline(line.trim()));
    });
  return blocks;
};

const runsToPlain = (runs) => runs.map((run) => run.text).join('');

// Plain text with all markup removed (no bullet markers). Used for word counting.
export const toPlainText = (text) =>
  parseProfileMarkup(text)
    .map((block) => {
      if (block.type === 'heading') {
        return runsToPlain(block.runs);
      }
      if (block.type === 'bulletList') {
        return block.items.map(runsToPlain).join('\n');
      }
      return block.lines.map(runsToPlain).join('\n');
    })
    .join('\n\n');

// ---------------------------------------------------------------------------
// Serializing: blocks -> text
// ---------------------------------------------------------------------------

const escapeText = (text) => text.replace(/[\\*]/g, (char) => `\\${char}`);

// Marks never start or end on whitespace, so "**bold** next" not "**bold **next".
const splitEdgeWhitespace = (runs) =>
  runs.flatMap((run) => {
    if (!run.bold && !run.italic) {
      return [run];
    }
    const [, lead, core, trail] = run.text.match(/^(\s*)([\s\S]*?)(\s*)$/);
    return [
      lead && { text: lead, bold: false, italic: false },
      core && { ...run, text: core },
      trail && { text: trail, bold: false, italic: false }
    ].filter(Boolean);
  });

export const serializeInline = (runs) => {
  let output = '';
  let bold = false;
  let italic = false;
  const transition = (nextBold, nextItalic) => {
    let markers = '';
    if (bold && !nextBold && italic && !nextItalic) {
      markers += '***';
    } else {
      if (italic && !nextItalic) {
        markers += '*';
      }
      if (bold && !nextBold) {
        markers += '**';
      }
      if (!bold && nextBold && !italic && nextItalic) {
        markers += '***';
        nextBold = true;
        nextItalic = true;
      } else {
        if (!bold && nextBold) {
          markers += '**';
        }
        if (!italic && nextItalic) {
          markers += '*';
        }
      }
    }
    output += markers;
    bold = nextBold;
    italic = nextItalic;
  };
  splitEdgeWhitespace(runs).forEach((run) => {
    if (!run.text) {
      return;
    }
    transition(Boolean(run.bold), Boolean(run.italic));
    output += escapeText(run.text);
  });
  transition(false, false);
  return output;
};

// A line of paragraph text must not be read back as a heading or bullet.
const escapeLineStart = (line) => (/^(#|-) /.test(line) ? `\\${line}` : line);

export const serializeProfileMarkup = (blocks) =>
  blocks
    .map((block) => {
      if (block.type === 'heading') {
        return `# ${serializeInline(block.runs)}`;
      }
      if (block.type === 'bulletList') {
        return block.items.map((runs) => `- ${serializeInline(runs)}`).join('\n');
      }
      return block.lines.map((runs) => escapeLineStart(serializeInline(runs))).join('\n');
    })
    .filter((chunk) => chunk.trim())
    .join('\n\n');

// ---------------------------------------------------------------------------
// TipTap (ProseMirror) JSON <-> text
// ---------------------------------------------------------------------------

const runsToEditorNodes = (runs) =>
  runs
    .filter((run) => run.text)
    .map((run) => {
      const marks = [run.bold && { type: 'bold' }, run.italic && { type: 'italic' }].filter(Boolean);
      return marks.length ? { type: 'text', text: run.text, marks } : { type: 'text', text: run.text };
    });

export const toEditorJson = (text) => {
  const content = parseProfileMarkup(text).map((block) => {
    if (block.type === 'heading') {
      return { type: 'heading', attrs: { level: 1 }, content: runsToEditorNodes(block.runs) };
    }
    if (block.type === 'bulletList') {
      return {
        type: 'bulletList',
        content: block.items.map((runs) => ({
          type: 'listItem',
          content: [{ type: 'paragraph', content: runsToEditorNodes(runs) }]
        }))
      };
    }
    const nodes = [];
    block.lines.forEach((runs, index) => {
      if (index > 0) {
        nodes.push({ type: 'hardBreak' });
      }
      nodes.push(...runsToEditorNodes(runs));
    });
    return { type: 'paragraph', content: nodes };
  });
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
};

const nodeToRuns = (node) => {
  const marks = (node.marks || []).map((mark) => mark.type);
  return { text: node.text || '', bold: marks.includes('bold'), italic: marks.includes('italic') };
};

// Splits inline content into lines at hard breaks.
const inlineToLines = (content = []) => {
  const lines = [[]];
  content.forEach((node) => {
    if (node.type === 'hardBreak') {
      lines.push([]);
    } else if (node.type === 'text') {
      lines[lines.length - 1].push(nodeToRuns(node));
    }
  });
  return lines;
};

const trimRuns = (runs) => {
  const copy = runs.map((run) => ({ ...run }));
  if (copy.length) {
    copy[0].text = copy[0].text.replace(/^\s+/, '');
    copy[copy.length - 1].text = copy[copy.length - 1].text.replace(/\s+$/, '');
  }
  return copy.filter((run) => run.text);
};

// Collects list item text, flattening any nested lists into the same flat list.
const collectListItems = (listNode, items) => {
  (listNode.content || []).forEach((item) => {
    let runs = [];
    const flush = () => {
      const trimmed = trimRuns(runs);
      if (trimmed.length) {
        items.push(trimmed);
      }
      runs = [];
    };
    (item.content || []).forEach((child) => {
      if (child.type === 'bulletList' || child.type === 'orderedList') {
        flush();
        collectListItems(child, items);
      } else {
        inlineToLines(child.content).forEach((line) => {
          if (runs.length && line.length) {
            runs.push({ text: ' ', bold: false, italic: false });
          }
          runs.push(...line);
        });
      }
    });
    flush();
  });
  return items;
};

export const fromEditorJson = (doc) => {
  const blocks = [];
  (doc?.content || []).forEach((node) => {
    if (node.type === 'heading') {
      const runs = trimRuns(inlineToLines(node.content).flatMap((line, index) =>
        index > 0 ? [{ text: ' ', bold: false, italic: false }, ...line] : line
      ));
      if (runs.length) {
        blocks.push({ type: 'heading', runs });
      }
    } else if (node.type === 'bulletList' || node.type === 'orderedList') {
      const items = collectListItems(node, []);
      if (items.length) {
        blocks.push({ type: 'bulletList', items });
      }
    } else if (node.type === 'paragraph') {
      const lines = inlineToLines(node.content).map(trimRuns);
      if (lines.some((line) => line.length)) {
        blocks.push({ type: 'paragraph', lines });
      }
    }
  });
  return serializeProfileMarkup(blocks);
};
